import { useLazyQuery } from "@apollo/client/react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";
import { Cross, Info, LoadingAnimated, Search } from "@pmag/icons";
import { Button, IconButton, Typography } from "@pmag/ui-components";
import { Routes } from "@src/context/router/RouterContext";
import { type GQLFulltextSearchModelsQuery, type GQLFulltextSearchModelsQueryVariables, type GQLModelFilter } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { fulltextSearchQuery } from "@src/pages/landingPage/components/searchContainer/Search.gql";
import { getSearchHintForTheme } from "@src/theme/theme.utils";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
    CloseInfoButton,
    ContainerBox,
    InfoBox,
    InputBox,
    LoadingBox,
    MediumInfoButton,
    MediumSearchButton,
    SearchBoxContainer,
    SmallBottomBox,
    SmallInfoButton,
    StyledSearchBox,
    StyledSearchBoxContainer,
    TopContainer,
} from "./SearchContainer.styles";

export const dataTestId = {
    container: "sparePartsFinder.landingPage.searchContainer.container",
    infoBox: "sparePartsFinder.landingPage.searchContainer.infoBox",
    infoButton: "sparePartsFinder.landingPage.searchContainer.infoButton",
    closeInfoButton: "sparePartsFinder.landingPage.searchContainer.closeInfoButton",
    submitButton: "sparePartsFinder.landingPage.searchContainer.submitButton",
    loadingBox: "sparePartsFinder.landingPage.searchContainer.loadingBox",
    hintText: "sparePartsFinder.landingPage.searchContainer.hintText",
    searchInput: "sparePartsFinder.landingPage.searchContainer.searchInput",
    clearSearchInputButton: "sparePartsFinder.landingPage.searchContainer.clearSearchInputButton",
};

export const SearchContainer: React.FunctionComponent = () => {
    const intl = useIntl();
    const theme = useTheme();
    const { setRoute } = useRouter();
    const { setSearchTerm, supportedBrandIds } = useGlobalState();
    const attributeConfig = useAttributeConfig();

    let filter: GQLModelFilter = {
        specification: attributeConfig.region,
        isWpArticle: { equal: false },
        hasChildren: false,
        hasVisibleDescendants: true,
    };
    if (supportedBrandIds.length > 0) {
        filter = { ...filter, brand: { isAnyOf: supportedBrandIds } };
    }

    const [showInfo, setShowInfo] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>("");

    const [fulltextSearch, { data, loading, error }] = useLazyQuery<GQLFulltextSearchModelsQuery, GQLFulltextSearchModelsQueryVariables>(
        fulltextSearchQuery,
    );

    const onSubmit = async () => {
        try {
            const { data } = await fulltextSearch({ variables: { query: searchValue, filter: filter, offset: 0 } });
            if (data && data.models.nodes.length > 0) {
                setSearchTerm(searchValue);
                setRoute(Routes.SearchResult);
            }
        } catch {
            // Apollo Client 4 rejects here on errors (and when the query is aborted); the error state comes from the hook's `error`
        }
    };

    const onKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter") void onSubmit();
    };

    const showNoResult = !!(error || (data && data.models.nodes.length == 0));

    return (
        <ContainerBox data-testid={dataTestId.container}>
            {showInfo ? (
                <InfoBox data-testid={dataTestId.infoBox}>
                    <Box mb={6}>
                        <Typography variant="heading-07">
                            <FormattedMessage id="sparePartsFinder.searchBox.info.title" defaultMessage="Where do I find my VIN?" />
                        </Typography>
                    </Box>

                    <Box mb={6}>
                        <Typography variant="body-short-02">
                            <FormattedMessage
                                id="sparePartsFinder.searchBox.info.description1"
                                defaultMessage="You can find the VIN of your vehicle in the vehicle registration document or on the type plate on your vehicle. The type plate can be mounted at various positions on your vehicle depending on type and model."
                            />
                        </Typography>
                    </Box>

                    <CloseInfoButton variant="text" icon={<Cross />} onClick={() => setShowInfo(false)} data-testid={dataTestId.closeInfoButton} />
                </InfoBox>
            ) : (
                <SearchBoxContainer>
                    <TopContainer>
                        <Typography variant="heading-07">
                            <FormattedMessage id="sparePartsFinder.searchBox.title" defaultMessage="search for your model" />
                        </Typography>

                        <MediumInfoButton
                            variant="text"
                            onClick={() => setShowInfo(true)}
                            startIcon={<Info size="small" />}
                            data-testid={dataTestId.infoButton}
                        >
                            <Typography variant="label-01">
                                <FormattedMessage id="sparePartsFinder.searchBox.vin" defaultMessage="Where do I find my VIN?" />
                            </Typography>
                        </MediumInfoButton>
                    </TopContainer>

                    <InputBox mb={2}>
                        <StyledSearchBoxContainer>
                            <StyledSearchBox
                                data-testid={dataTestId.searchInput}
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.currentTarget.value)}
                                onKeyPress={onKeyPress}
                                fullWidth={true}
                                placeholder={intl.formatMessage({
                                    id: "sparePartsFinder.searchBox.textInput.placeholder",
                                    defaultMessage: "Type VIN or model name",
                                })}
                                endAdornment={
                                    searchValue !== "" && (
                                        <IconButton
                                            variant="transparent"
                                            onClick={() => setSearchValue("")}
                                            data-testid={dataTestId.clearSearchInputButton}
                                        >
                                            <Cross />
                                        </IconButton>
                                    )
                                }
                                error={showNoResult}
                            />
                        </StyledSearchBoxContainer>

                        <MediumSearchButton variant="primary" onClick={onSubmit} icon={<Search size="small" />} data-testid={dataTestId.submitButton}>
                            <FormattedMessage id="sparePartsFinder.searchBox.submitButtonTitle" defaultMessage="Search" />
                        </MediumSearchButton>
                    </InputBox>

                    <Typography variant="helper-text-01" color={showNoResult ? theme.palette.red._50 : undefined} data-testid={dataTestId.hintText}>
                        {showNoResult ? (
                            <FormattedMessage id="sparePartsFinder.searchBox.error" defaultMessage="No result. Please try again." />
                        ) : (
                            getSearchHintForTheme(intl, attributeConfig.theme)
                        )}
                    </Typography>

                    <SmallBottomBox>
                        <Button variant="primary" onClick={onSubmit} icon={<Search size="small" />} data-testid={dataTestId.submitButton}>
                            <FormattedMessage id="sparePartsFinder.searchBox.submitButtonTitle" defaultMessage="Search" />
                        </Button>

                        <SmallInfoButton variant="text" onClick={() => setShowInfo(true)} startIcon={<Info size="small" />}>
                            <Typography variant="body-short-01">
                                <FormattedMessage id="sparePartsFinder.searchBox.vin" defaultMessage="Where do I find my VIN?" />
                            </Typography>
                        </SmallInfoButton>
                    </SmallBottomBox>

                    {loading && (
                        <LoadingBox data-testid={dataTestId.loadingBox}>
                            <LoadingAnimated />
                        </LoadingBox>
                    )}
                </SearchBoxContainer>
            )}
        </ContainerBox>
    );
};
